export enum DateRangeOption {
  LAST_DAY = 1,
  LAST_7_DAYS,
  LAST_30_DAYS,
  LAST_12_MONTHS,
  CUSTOM
}

export enum DateOption {
  NOW,
  THIRTY_MINUTES,
  ONE_DAY,
  ONE_WEEK,
  NEVER,
  CUSTOM
}

export const allDateRangeOptions: DateRangeOption[] = [
  DateRangeOption.LAST_DAY,
  DateRangeOption.LAST_7_DAYS,
  DateRangeOption.LAST_30_DAYS,
  DateRangeOption.LAST_12_MONTHS,
  DateRangeOption.CUSTOM
]

const hourInMillis = 1000 * 60 * 60
export const dayInMillis = hourInMillis * 24

function getDateRangeToNow(durationInMillis: number): DateRange {
  return {
    start: new Date(Date.now() - durationInMillis),
    end: new Date(Date.now())
  }
}

export function getDateRangeToNowCurried(
  durationInMillis: number
): () => DateRange {
  return () => getDateRangeToNow(durationInMillis)
}

export const dateRangeOptionToRange: Record<
  DateRangeOption,
  () => DateRange | undefined
> = {
  [DateRangeOption.LAST_DAY]: getDateRangeToNowCurried(dayInMillis),
  [DateRangeOption.LAST_7_DAYS]: getDateRangeToNowCurried(7 * dayInMillis),
  [DateRangeOption.LAST_30_DAYS]: getDateRangeToNowCurried(30 * dayInMillis),
  [DateRangeOption.LAST_12_MONTHS]: () => ({
    start: new Date(
      new Date(Date.now()).setFullYear(new Date(Date.now()).getFullYear() - 1)
    ),
    end: new Date(Date.now())
  }),
  [DateRangeOption.CUSTOM]: () => undefined
}

export interface DayzedDate {
  readonly date: Date
  readonly nextMonth: boolean
  readonly prevMonth: boolean
  readonly selectable: boolean
  readonly selected: boolean
  readonly today: boolean
}

export type DayzedWeek = Array<DayzedDate | string>

export interface DayzedCalendar {
  readonly month: number
  readonly year: number
  readonly firstDayOfMonth: Date
  readonly lastDayOfMonth: Date
  readonly weeks: DayzedWeek[]
}

interface DayzedGetDatePropsOptions extends React.HTMLProps<HTMLButtonElement> {
  readonly dateObj: DayzedDate
}

interface DayzedGetButtonPropsOptions
  extends React.HTMLProps<HTMLButtonElement> {
  readonly offset?: number
  readonly calendars: DayzedCalendar[]
}

export type DatePropsGetter = (options: DayzedGetDatePropsOptions) => any
export type DateButtonGetter = (options: DayzedGetButtonPropsOptions) => any

export interface DayzedRenderProps {
  readonly calendars: DayzedCalendar[]
  readonly getDateProps: DatePropsGetter
  readonly getBackProps: DateButtonGetter
  readonly getForwardProps: DateButtonGetter
}

export interface DateRange {
  readonly start: Date
  readonly end: Date
}

export type DateOrNow = Date | 'NOW'

export function getDateFromDateOrNow(dateOrNow: DateOrNow) {
  if (dateOrNow === 'NOW') {
    return new Date(Date.now())
  }
  return dateOrNow
}

export function isValidEndDate(dateOrNow: DateOrNow, endDate: Date) {
  if (dateOrNow === 'NOW') {
    return new Date(new Date().setHours(23, 59, 59)) < endDate
  }
  return dateOrNow < endDate
}

export interface TimeRange {
  readonly startHour: number
  readonly startMinute: number
  readonly endHour: number
  readonly endMinute: number
}

export interface Time {
  readonly hour: number
  readonly minute: number
}

function getValidTimeRange(
  { endHour, startHour, endMinute, startMinute }: TimeRange,
  defaultToStart: boolean
): TimeRange {
  if (
    endHour < startHour ||
    (startHour === endHour && endMinute <= startMinute)
  ) {
    const newStartHour =
      endHour >= startHour || defaultToStart ? startHour : endHour
    const newEndHour =
      endHour >= startHour || !defaultToStart ? endHour : startHour

    if (endHour <= startHour && endMinute <= startMinute) {
      let newStartMinute = defaultToStart ? startMinute : endMinute - 1
      let newEndMinute = defaultToStart ? startMinute + 1 : endMinute

      if (newStartMinute < 0) {
        newStartMinute = 0
        newEndMinute = 1
      } else if (newEndMinute > 59) {
        newStartMinute = 58
        newEndMinute = 59
      }

      return {
        startHour: newStartHour,
        endHour: newEndHour,
        startMinute: newStartMinute,
        endMinute: newEndMinute
      }
    }

    return {
      startHour: newStartHour,
      endHour: newEndHour,
      startMinute,
      endMinute
    }
  }
  return {
    endHour,
    startHour,
    endMinute,
    startMinute
  }
}

export function getValidTimeRangeDefaultToStart(
  timeRange: TimeRange
): TimeRange {
  return getValidTimeRange(timeRange, true)
}

export function getValidTimeRangeDefaultToEnd(timeRange: TimeRange): TimeRange {
  return getValidTimeRange(timeRange, false)
}

function getValidNumberInRange(value: number, min: number, max: number) {
  if (value < min) {
    return min
  } else if (value > max) {
    return max
  }
  return value
}

export function getValidHour(hour: number) {
  return getValidNumberInRange(hour, 0, 23)
}

export function getValidMinute(minute: number) {
  return getValidNumberInRange(minute, 0, 59)
}

export function dateToToLongMonthString(date: Date, locale: string) {
  return new Intl.DateTimeFormat(locale, {
    month: 'long'
  }).format(date)
}

export function getDayAbbreviations(locale: string) {
  const getDayOfWeek = (day) => {
    const date = new Date(0)
    date.setDate(date.getDate() - date.getDay() + day)
    return date
  }

  const dayAbbreviations: string[] = []
  for (let i = 0; i < 7; i++) {
    dayAbbreviations.push(
      new Intl.DateTimeFormat(locale, {
        weekday: 'narrow'
      }).format(getDayOfWeek(i))
    )
  }
  return dayAbbreviations
}
